import React from 'react'

import Gallery from '../../../../../components/pdf/Gallery'
import Image from '../../../../../components/pdf/Image'
import Separator from '../../../../../components/pdf/Separator'
import View from '../../../../../components/pdf/View'
import { SectionHeading } from './_shared/Prose'
import PlaceholderWarning from './_shared/PlaceholderWarning'

/**
 * Seção 21 - Certificado de Calibração. No modelo fornecido este é um anexo/imagem de um
 * certificado escaneado de terceiros (laboratório de calibração INSTRUTEMP).
 *
 * FONTE DO CERTIFICADO (corrigido nesta revisão — pedido de Julia Buzzo, PM, 2026-08-27:
 * "o certificado de calibração tem que vir do usuário que executou [a atividade], não da
 * atividade"): `certificadoExecutor` é o Campo Adicional "Certificado de Calibração" da
 * CONTA do usuário que executou o serviço (`serviceOrder.user`), resolvido por
 * `getExecutorCertificadoCalibracao` (`_helpers/certificadoEngine.js`) a partir de
 * `ExecutorCertificadoLoader.js` — NUNCA das respostas (`replies`) da atividade em si, ao
 * contrário do padrão usado por outros módulos de PDF (ex.:
 * `bioqualynet/aet/sections/08.MeasuringEquipmentSection.js`, que lê o certificado das
 * respostas de uma atividade de certificação vinculada). Quando presente, é renderizado
 * abaixo via `Gallery` (mesmo componente/padrão já usado para Campo Adicional do tipo
 * "gallery" em outros PDFs, ver `lmdasilva/general/CustomFields.js`).
 *
 * QUANDO AUSENTE (sem executor identificado, executor sem esse Campo Adicional
 * configurado na conta, ou campo vazio): `certificadoExecutor` chega como `null`/`undefined`
 * e a seção cai de volta no exemplo estático do modelo aprovado (com o aviso de
 * placeholder abaixo) em vez de deixar a seção vazia ou quebrar a geração do PDF — mesmo
 * comportamento de antes desta revisão.
 *
 * LIMITAÇÃO ASSUMIDA (exemplo estático, usado como fallback): o certificado é específico de
 * um equipamento (calibrador CASELLA CEL-110/2, nº de série 248326) e de uma data de
 * calibração — portanto varia por dependência. A imagem abaixo é o exemplo do modelo
 * aprovado e serve de fallback enquanto o executor não tiver o certificado real cadastrado
 * na própria conta (ver aviso em `PlaceholderWarning` logo abaixo do título).
 *
 * A pedido de Julia Buzzo ("coloque a imagem no ponto 21 igual o documento modelo"), o certificado
 * de exemplo foi extraído da página 56 do PDF modelo e é exibido abaixo como imagem. Arquivo:
 * `public/pdfs/bradesco/certificado-calibracao-exemplo.png`.
 *
 * A imagem de exemplo é composta pelos objetos de imagem (XObjects) embutidos no PDF — corpo do
 * certificado + faixa de assinaturas/rodapé, remontados na posição original — em vez de um render
 * da página completa. Isso evita capturar a marca d'água diagonal "EXEMPLO"/"NÃO REPRODUZIR", que
 * no PDF modelo é uma camada de overlay separada sobre a página, não parte das imagens embutidas. A
 * pedido de Julia Buzzo ("e essa imagem não pode ter esse escrito em vermelho no fundo"), a
 * imagem exibida não contém essa marca d'água.
 *
 * A reprodução redundante do certificado como texto/tabela (que existia antes da imagem real
 * estar disponível) foi removida a pedido de Julia Buzzo ("essa parte pode tirar") — a imagem
 * acima já mostra o mesmo conteúdo.
 */
const CertificadoCalibracaoSection = ({ certificadoExecutor }) => (
  <View id="21">
    <Separator weight={15} />
    <SectionHeading numero={21}>CERTIFICADO DE CALIBRAÇÃO</SectionHeading>

    {certificadoExecutor ? (
      <Gallery items={certificadoExecutor} />
    ) : (
      <>
        <PlaceholderWarning title="[EXEMPLO DO MODELO] Substituir pelo certificado real desta dependência quando disponível.">
          O executor desta atividade não possui um Certificado de Calibração cadastrado na própria conta — o
          conteúdo abaixo reproduz o certificado de calibração do modelo aprovado (INSTRUTEMP, calibrador acústico
          CASELLA CEL-110/2, OS 50757, calibrado em 26/05/2025) como exemplo, pois o certificado real de cada
          dependência é específico por equipamento e data de calibração.
        </PlaceholderWarning>

        <Separator weight={10} />

        <View style={{ display: 'flex', justifyContent: 'center' }}>
          <Image
            src="/pdfs/bradesco/certificado-calibracao-exemplo.png"
            maxWidth={480}
            ignoreFixedSize
            alt="Certificado de Calibração INSTRUTEMP - exemplo do modelo"
          />
        </View>
      </>
    )}

    <Separator weight={15} />
  </View>
)

export default CertificadoCalibracaoSection
